import classNames from 'classnames';
import {Provider} from 'mobx-react';
import {useState} from 'react';

import {AsyncPriceCourseCardContext} from '@udemy/browse-course';
import {Academy, getProfessionalSkillsShowcaseData} from '@udemy/career-academies';
import {FunnelLogContextProvider} from '@udemy/funnel-tracking';
import {useI18n} from '@udemy/i18n';
import {DiscoveryUnitsContainerStore, ProfessionalSkillsUnit} from '@udemy/react-discovery-units';
import {CourseCardSkeletonGroup} from '@udemy/react-reveal-components';
import {useUDData} from '@udemy/ud-data';

import {Topic} from '../topic-links/types';
import styles from './academies-course-exploration.module.less';

interface AcademyCourseExplorationProps {
    academy: Academy;
    topics?: Topic[];
}

const renderCourseExplorationSkeleton = () => {
    const CustomCardWrapper = ({children}: {children: React.ReactNode}) => (
        <div className={styles['loader']}>{children}</div>
    );

    return <CourseCardSkeletonGroup cardCountPerRow={4} cardWrapper={CustomCardWrapper} />;
};

export const AcademiesCourseExploration = ({
    academy,
    topics,
}: AcademyCourseExplorationProps): JSX.Element | null => {
    const udData = useUDData();
    const {gettext} = useI18n();
    const [discoveryUnitsContainerStore] = useState(
        // TODO Swap out pageType value with the real academies key once available
        () => new DiscoveryUnitsContainerStore({pageType: 'logged_out_homepage'}, udData),
    );

    if (!academy) {
        return null;
    }

    const professionalSkillsShowcaseData = getProfessionalSkillsShowcaseData(academy);

    // Inject links to topics
    if (professionalSkillsShowcaseData?.available_filters?.units) {
        professionalSkillsShowcaseData.available_filters.units =
            professionalSkillsShowcaseData.available_filters.units.map((unit) => {
                return {
                    ...unit,
                    source_objects: [
                        {
                            url:
                                topics?.find(
                                    (topic) =>
                                        topic.id === unit.available_filters?.units?.[0]?.label_id,
                                )?.link ?? '',
                        },
                    ],
                };
            });
    }

    return (
        <div className={styles['section-parent-container']}>
            <div className={classNames('ud-container', styles['course-exploration-container'])}>
                <FunnelLogContextProvider pageType={'logged_out_homepage'}>
                    <Provider discoveryUnitsStore={discoveryUnitsContainerStore}>
                        <AsyncPriceCourseCardContext.Provider
                            value={{useCourseProductCard: true, hideCourseDescription: true}}
                        >
                            <ProfessionalSkillsUnit
                                heading={interpolate(
                                    gettext('Discover more courses for %(careerName)s careers'),
                                    {careerName: academy.career.name},
                                    true,
                                )}
                                subheading={''}
                                className={styles['professional-skills-unit']}
                                contentPanelClassName={styles['panel-content']}
                                showSkillFilters={false}
                                unitData={professionalSkillsShowcaseData}
                                contentLoadingSkeleton={renderCourseExplorationSkeleton()}
                                carouselProps={{
                                    ariaLabel: interpolate(
                                        gettext('Courses for %(careerName)s careers'),
                                        {careerName: academy.career.name},
                                        true,
                                    ),
                                }}
                            />
                        </AsyncPriceCourseCardContext.Provider>
                    </Provider>
                </FunnelLogContextProvider>
            </div>
        </div>
    );
};
